<template>
  <el-dialog
    title="生成/更新统计"
    :visible.sync="dialogVisible"
    width="580px"
    :close-on-click-modal="false"
    @open="onOpen"
  >
    <el-form ref="form" :model="form" :rules="rules" label-width="110px" size="small">
      <!-- 报告类型 -->
      <el-form-item label="报告类型" prop="reportType">
        <el-radio-group v-model="form.reportType" @change="onTypeChange">
          <el-radio :label="1">月度报告</el-radio>
          <el-radio :label="2">季度报告</el-radio>
          <el-radio :label="3">年度报告</el-radio>
        </el-radio-group>
      </el-form-item>

      <!-- 年度（始终显示） -->
      <el-form-item label="年度" prop="year">
        <el-select v-model="form.year" @change="onYearChange" style="width: 130px">
          <el-option v-for="y in yearOptions" :key="y" :label="y + '年'" :value="y" />
        </el-select>
      </el-form-item>

      <!-- 月度模式：月份 -->
      <el-form-item v-if="form.reportType === 1" label="月份" prop="month">
        <el-select v-model="form.month" style="width: 130px" placeholder="请选择月份">
          <el-option v-for="m in 12" :key="m" :label="m + '月'" :value="m" />
        </el-select>
        <div class="hint-text">月度报告会反查该年月已结束的波次进行聚合</div>
      </el-form-item>

      <!-- 季度模式：季度 -->
      <el-form-item v-if="form.reportType === 2" label="季度" prop="quarter">
        <el-select v-model="form.quarter" @change="onQuarterChange" style="width: 130px" placeholder="请选择季度">
          <el-option v-for="q in 4" :key="q" :label="'Q' + q + ' (' + quarterMonths(q) + '月)'" :value="q" />
        </el-select>
      </el-form-item>

      <!-- 季度模式：合并月度报告源 -->
      <el-form-item v-if="form.reportType === 2" label="合并月度报告" prop="selectedSnapshotIds">
        <div v-if="loadingSources" class="hint-text">加载可用月度报告...</div>
        <div v-else-if="filteredSources.length === 0" class="empty-tip">
          所选季度下暂无月度报告，请先在该季度的每个月生成月度报告
        </div>
        <el-checkbox-group v-else v-model="form.selectedSnapshotIds">
          <div v-for="s in filteredSources" :key="s.snapshotId" class="source-item">
            <el-checkbox :label="s.snapshotId">
              {{ s.periodLabel }}
              <el-tag size="mini" :type="s.isPublished ? 'success' : 'info'" effect="plain">
                {{ s.isPublished ? '已发布' : '草稿' }}
              </el-tag>
              <span class="source-meta">{{ s.storeCount }} 门店</span>
              <span v-if="s.scoringRuleCode" class="source-meta">规则: {{ scoringRuleLabel(s.scoringRuleCode) }}</span>
            </el-checkbox>
          </div>
        </el-checkbox-group>
      </el-form-item>

      <!-- 年度模式：合并方式 -->
      <el-form-item v-if="form.reportType === 3" label="合并方式" prop="mergeMode">
        <el-radio-group v-model="form.mergeMode" @change="onMergeModeChange">
          <el-radio :label="1">按月度合并</el-radio>
          <el-radio :label="2">按季度合并</el-radio>
        </el-radio-group>
      </el-form-item>

      <!-- 年度模式：合并源 -->
      <el-form-item v-if="form.reportType === 3" label="合并报告" prop="selectedSnapshotIds">
        <div v-if="loadingSources" class="hint-text">加载可用报告...</div>
        <div v-else-if="filteredSources.length === 0" class="empty-tip">
          所选年度下暂无{{ form.mergeMode === 1 ? '月度' : '季度' }}报告
        </div>
        <el-checkbox-group v-else v-model="form.selectedSnapshotIds">
          <div v-for="s in filteredSources" :key="s.snapshotId" class="source-item">
            <el-checkbox :label="s.snapshotId">
              {{ s.periodLabel }}
              <el-tag size="mini" :type="s.isPublished ? 'success' : 'info'" effect="plain">
                {{ s.isPublished ? '已发布' : '草稿' }}
              </el-tag>
              <span class="source-meta">{{ s.storeCount }} 门店</span>
              <span v-if="s.scoringRuleCode" class="source-meta">规则: {{ scoringRuleLabel(s.scoringRuleCode) }}</span>
            </el-checkbox>
          </div>
        </el-checkbox-group>
        <div v-if="filteredSources.length > 0" class="select-actions">
          <el-button type="text" size="mini" @click="selectAll">全选</el-button>
          <el-button type="text" size="mini" @click="clearSelection">清空</el-button>
        </div>
      </el-form-item>

      <div class="dialog-tip">报告生成后保存为快照，可重复生成覆盖。</div>
    </el-form>

    <div slot="footer">
      <el-button size="small" @click="dialogVisible = false">取消</el-button>
      <el-button size="small" type="primary" :loading="generating" @click="handleConfirm">生成</el-button>
    </div>
  </el-dialog>
</template>

<script>
import axios from 'index/axios'

const RULE_LABELS = {
  simple: '原始累加',
  percentage: '百分制',
  bonusNormalized: '加分题百分制',
  custom: '自定义'
}

export default {
  name: 'GenerateReportDialog',
  props: {
    visible: { type: Boolean, default: false },
    projectId: { type: [Number, String], required: true },
    formKey: { type: String, required: true },
    formName: { type: String, default: '' }
  },
  data () {
    return {
      form: {
        reportType: 1,
        year: new Date().getFullYear(),
        month: null,
        quarter: null,
        mergeMode: 1,
        selectedSnapshotIds: []
      },
      availableSources: [],
      loadingSources: false,
      generating: false,
      rules: {
        reportType: [{ required: true, message: '请选择报告类型', trigger: 'change' }],
        year: [{ required: true, message: '请选择年度', trigger: 'change' }],
        month: [{ required: true, message: '请选择月份', trigger: 'change' }],
        quarter: [{ required: true, message: '请选择季度', trigger: 'change' }],
        mergeMode: [{ required: true, message: '请选择合并方式', trigger: 'change' }],
        selectedSnapshotIds: [{
          validator: (rule, value, callback) => {
            if (this.form.reportType >= 2 && (!value || value.length === 0)) {
              callback(new Error('请至少选择一个报告作为合并源'))
            } else { callback() }
          },
          trigger: 'change'
        }]
      }
    }
  },
  computed: {
    dialogVisible: {
      get () { return this.visible },
      set (v) { this.$emit('update:visible', v) }
    },
    yearOptions () {
      const cur = new Date().getFullYear()
      return [cur - 2, cur - 1, cur, cur + 1]
    },
    filteredSources () {
      if (this.form.reportType === 2 && this.form.quarter) {
        const qStart = (this.form.quarter - 1) * 3 + 1
        const qEnd = qStart + 2
        return this.availableSources.filter(s => s.month && s.month >= qStart && s.month <= qEnd)
      }
      return this.availableSources
    }
  },
  methods: {
    onOpen () {
      this.form = {
        reportType: 1,
        year: new Date().getFullYear(),
        month: null,
        quarter: null,
        mergeMode: 1,
        selectedSnapshotIds: []
      }
      this.availableSources = []
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate()
      })
    },
    onTypeChange (type) {
      this.form.month = null
      this.form.quarter = null
      this.form.selectedSnapshotIds = []
      if (type >= 2) this.loadAvailableSources()
    },
    onYearChange () {
      this.form.month = null
      this.form.quarter = null
      this.form.selectedSnapshotIds = []
      if (this.form.reportType >= 2) this.loadAvailableSources()
    },
    onQuarterChange () {
      this.form.selectedSnapshotIds = []
    },
    onMergeModeChange () {
      this.form.selectedSnapshotIds = []
      this.loadAvailableSources()
    },
    quarterMonths (q) {
      const start = (q - 1) * 3 + 1
      return `${start}-${start + 2}`
    },
    scoringRuleLabel (code) {
      return RULE_LABELS[code] || code
    },
    selectAll () {
      this.form.selectedSnapshotIds = this.filteredSources.map(s => s.snapshotId)
    },
    clearSelection () {
      this.form.selectedSnapshotIds = []
    },
    async loadAvailableSources () {
      if (this.form.reportType < 2) return
      const periodType = this.form.reportType === 3 && this.form.mergeMode === 2 ? 2 : 1
      this.loadingSources = true
      try {
        const res = await axios.post('/Report/availableSources', {
          ProjectId: this.projectId,
          FormKey: this.formKey,
          Year: this.form.year,
          PeriodType: periodType
        })
        // v1.5 修复 P2-02：兼容 axios wrapper 的多层结构
        // 可能形态：{ items }、{ data: { items } }、{ data: { data: { items } } }
        const wrapper = (res && res.data) || res || {}
        const items = wrapper.items ||
          (wrapper.data && wrapper.data.items) ||
          (wrapper.data && wrapper.data.data && wrapper.data.data.items) ||
          []
        this.availableSources = items
      } catch (e) {
        this.availableSources = []
        this.$message.error('加载可用报告失败')
      } finally {
        this.loadingSources = false
      }
    },
    async handleConfirm () {
      try {
        await this.$refs.form.validate()
      } catch {
        return
      }

      // 前端校验：合并源算分规则一致
      if (this.form.reportType >= 2) {
        const codes = [...new Set(
          this.availableSources
            .filter(s => this.form.selectedSnapshotIds.includes(s.snapshotId))
            .map(s => s.scoringRuleCode)
            .filter(Boolean)
        )]
        if (codes.length > 1) {
          this.$message.error('选中的源报告算分规则不一致，无法合并')
          return
        }
      }

      const payload = {
        ProjectId: this.projectId,
        FormKey: this.formKey,
        ReportType: this.form.reportType,
        Year: this.form.year,
        Month: this.form.reportType === 1 ? this.form.month : null,
        Quarter: this.form.reportType === 2 ? this.form.quarter : null,
        MergeMode: this.form.reportType === 3 ? this.form.mergeMode : 1,
        SourceMonthlySnapshotIds: this.form.reportType === 2 ? this.form.selectedSnapshotIds : null,
        SourceSnapshotIds: this.form.reportType === 3 ? this.form.selectedSnapshotIds : null
      }

      this.generating = true
      try {
        const res = await axios.post('/Report/generateV2', payload)
        const ok = (res && (res.errcode === 0 || (res.data && res.data.errcode === 0)))
        if (ok) {
          this.$message.success((res.message || (res.data && res.data.message)) || '报告已生成')
          this.dialogVisible = false
          this.$emit('success', {
            reportType: this.form.reportType,
            year: this.form.year,
            month: this.form.month,
            quarter: this.form.quarter,
            snapshotId: (res.snapshotId || (res.data && res.data.snapshotId)),
            periodKey: (res.periodKey || (res.data && res.data.periodKey)),
            periodLabel: (res.periodLabel || (res.data && res.data.periodLabel))
          })
        } else {
          const msg = (res && (res.message || (res.data && res.data.message))) || '生成失败'
          this.$message.error(msg)
        }
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || '生成失败'
        this.$message.error(msg)
      } finally {
        this.generating = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.hint-text {
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.6;
  margin-top: 4px;
}
.empty-tip {
  color: #94a3b8;
  font-size: 12px;
  background: #f8fafc;
  border: 1px dashed #e2e8f0;
  padding: 8px 12px;
  border-radius: 4px;
}
.source-item {
  margin-bottom: 6px;
  padding: 6px 8px;
  border: 1px solid #eef2f6;
  border-radius: 4px;
  background: #fafbfd;
  ::v-deep .el-checkbox__label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
}
.source-meta {
  color: #64748b;
  font-size: 12px;
}
.select-actions {
  margin-top: 4px;
}
.dialog-tip {
  color: #94a3b8;
  font-size: 12px;
  margin-top: 8px;
  padding-left: 110px;
}
</style>
